import { useEffect, useState } from "react";

/**
 * Sprint 29.2 Wave H — narrow-chrome breakpoint.
 *
 * `useIsMobile` (max-width 767 px) governs the layout decision for
 * pages like /map (single column vs. two-column grid). It does NOT
 * fit the AppShell nav strip: at 768 ≤ width ≤ 1023, the inline
 * desktop nav strip (13 items + role chip + email + locale + theme
 * + logout) doesn't fit, which Devin tracked as a P3 overflow.
 *
 * This hook returns `true` whenever the viewport is narrower than
 * 1024 px so the AppShell can collapse the nav strip into the
 * existing mobile drawer at tablet sizes too. The map layout's
 * sidebar / column decision still uses `useIsMobile`; this hook is
 * used only by chrome that doesn't fit a two-row tablet header.
 *
 * SSR-safe: defaults to `false` when `window` / `matchMedia` is
 * unavailable. The first browser-side commit resolves it correctly
 * via `useEffect`.
 */
export function useIsNarrowChrome(): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(NARROW_CHROME_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW_CHROME_QUERY);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mq.matches); // resync after mount
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return narrow;
}

export const NARROW_CHROME_QUERY = "(max-width: 1023px)";
